import { apiRequest, buildApiUrl, readJsonOrThrow } from "@/api/client";
import { getEffectiveAuthToken } from "@/utils/impersonation";

function appendIfPresent(searchParams, key, value) {
  if (value === undefined || value === null || value === "") {
    return;
  }
  searchParams.set(key, String(value));
}

export async function fetchTwitchClipLibrary(
  session,
  {
    days = 7,
    limit = null,
    ownClipsOnly = false,
    assignedAdminOnly = false,
    includeJobs = false,
    includeConverter = false,
  } = {}
) {
  const query = new URLSearchParams();
  appendIfPresent(query, "days", days);
  appendIfPresent(query, "limit", limit);
  if (ownClipsOnly) {
    query.set("own_clips_only", "true");
  }
  if (assignedAdminOnly) {
    query.set("assigned_admin_only", "true");
  }
  if (includeJobs) {
    query.set("include_jobs", "true");
  }
  if (includeConverter) {
    query.set("include_converter", "true");
  }

  const response = await apiRequest(`/momo/twitch/clip-library?${query.toString()}`, {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch clip library");
}

export async function fetchTwitchJobClips(session, jobId) {
  const query = new URLSearchParams();
  appendIfPresent(query, "job_id", jobId);
  query.set("compact", "true");

  const response = await apiRequest(`/momo/twitch/clips?${query.toString()}`, {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch job clips");
}

export async function fetchTwitchJobs(session, { days = 7, ownClipsOnly = false } = {}) {
  const query = new URLSearchParams();
  appendIfPresent(query, "days", days);
  if (ownClipsOnly) {
    query.set("own_clips_only", "true");
  }

  const response = await apiRequest(`/momo/twitch/jobs?${query.toString()}`, {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch jobs");
}

export async function fetchTwitchMetadataPreview(session, payload) {
  const response = await apiRequest("/momo/twitch/metadata-preview", {
    method: "POST",
    session,
    body: payload,
  });
  return readJsonOrThrow(response, "Failed to fetch metadata preview");
}

export async function fetchTwitchPostingTimeline(session) {
  const response = await apiRequest("/momo/twitch/posting-timeline", {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch posting timeline");
}

export async function fetchTwitchSchedulePreview(session, clipId) {
  const response = await apiRequest(
    `/momo/twitch/clips/${encodeURIComponent(clipId)}/schedule-preview`,
    {
      session,
      includeContentType: false,
    }
  );
  return readJsonOrThrow(response, "Failed to fetch schedule preview");
}

export async function fetchTwitchClipTranscript(session, clipId) {
  const response = await apiRequest(`/momo/twitch/clips/${encodeURIComponent(clipId)}/transcript`, {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch clip transcript");
}

export async function fetchTwitchAdminResults(session, jobId) {
  const response = await apiRequest(
    `/momo/twitch/admin/results/${encodeURIComponent(jobId)}`,
    {
      session,
      includeContentType: false,
    }
  );
  return readJsonOrThrow(response, "Failed to fetch admin results");
}

export async function fetchTwitchJobMediaSyncStatus(session, jobId) {
  const response = await apiRequest(
    `/momo/twitch/admin/media/jobs/${encodeURIComponent(jobId)}/sync-status`,
    {
      session,
      includeContentType: false,
    }
  );
  return readJsonOrThrow(response, "Failed to fetch media sync status");
}

export async function fetchTwitchMediaExpiryStatus(session, { limit = 100 } = {}) {
  const query = new URLSearchParams();
  appendIfPresent(query, "limit", limit);
  const response = await apiRequest(
    `/momo/twitch/admin/media/expiry?${query.toString()}`,
    {
      session,
      includeContentType: false,
    }
  );
  return readJsonOrThrow(response, "Failed to fetch media expiry status");
}

export async function requestTwitchClipDownload(session, clipId, requestId) {
  const response = await apiRequest(
    `/momo/twitch/clips/${encodeURIComponent(clipId)}/download`,
    {
      method: "POST",
      session,
      body: { request_id: requestId },
    }
  );
  return readJsonOrThrow(response, "Failed to request clip download");
}

export function buildTwitchClipPrivateMediaUrl(session, clipId) {
  const authToken = getEffectiveAuthToken(session);
  if (!authToken) {
    throw new Error("Authentication required");
  }
  return buildApiUrl(
    `/momo/twitch/clips/${encodeURIComponent(clipId)}/edit-source.mp4`
    + `?auth_token=${encodeURIComponent(authToken)}`
  );
}

export async function deactivateTwitchClip(session, clipId) {
  const response = await apiRequest(
    `/momo/twitch/clips/${encodeURIComponent(clipId)}/deactivate`,
    {
      method: "POST",
      session,
      includeContentType: false,
    }
  );
  return readJsonOrThrow(response, "Failed to deactivate clip");
}

export async function deleteTwitchClip(session, clipId) {
  const response = await apiRequest(`/momo/twitch/clips/${encodeURIComponent(clipId)}`, {
    method: "DELETE",
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to delete clip");
}

export async function updateTwitchClip(session, clipId, payload) {
  const response = await apiRequest(`/momo/twitch/clips/${encodeURIComponent(clipId)}`, {
    method: "PATCH",
    session,
    body: payload,
  });
  return readJsonOrThrow(response, "Failed to update clip");
}

export async function reassignTwitchJob(session, jobId, userId) {
  const response = await apiRequest(`/momo/twitch/jobs/${encodeURIComponent(jobId)}/reassign`, {
    method: "POST",
    session,
    body: { target_user_id: userId },
  });
  return readJsonOrThrow(response, "Failed to reassign job");
}

export async function createTwitchJob(session, payload) {
  const response = await apiRequest("/momo/twitch/jobs", {
    method: "POST",
    session,
    body: payload,
  });
  return readJsonOrThrow(response, "Failed to create job");
}

export async function createTwitchUploadSession(session, payload) {
  const response = await apiRequest("/momo/twitch/uploads", {
    method: "POST",
    session,
    body: payload,
  });
  return readJsonOrThrow(response, "Failed to create upload session");
}

async function readDirectUploadResponse(response, fallbackMessage) {
  return readJsonOrThrow(response, fallbackMessage);
}

export async function fetchTwitchUploadState(uploadUrl, uploadToken) {
  const response = await fetch(uploadUrl, {
    headers: {
      Authorization: `Bearer ${uploadToken}`,
    },
  });
  return readDirectUploadResponse(response, "Failed to fetch upload state");
}

export async function uploadTwitchFileChunk(
  uploadUrl,
  uploadToken,
  chunk,
  offset,
  totalBytes
) {
  const response = await fetch(uploadUrl, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${uploadToken}`,
      "Upload-Offset": String(offset),
      "Upload-Length": String(totalBytes),
      "Content-Type": "application/octet-stream",
    },
    body: chunk,
  });
  return readDirectUploadResponse(response, "Failed to upload file chunk");
}

export async function completeTwitchUpload(uploadUrl, uploadToken) {
  const response = await fetch(`${uploadUrl}/complete`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${uploadToken}`,
    },
  });
  return readDirectUploadResponse(response, "Failed to complete upload");
}

export async function createTwitchJobsBatch(session, payload) {
  const response = await apiRequest("/momo/twitch/jobs/batch", {
    method: "POST",
    session,
    body: payload,
  });
  return readJsonOrThrow(response, "Failed to create jobs");
}

export async function fetchUserIdByUserInfoId(session, userInfoId) {
  const response = await apiRequest(
    `/momo/twitch/user-info/user-id?user_info_id=${encodeURIComponent(userInfoId)}`,
    {
      session,
      includeContentType: false,
    }
  );
  return readJsonOrThrow(response, "Failed to resolve user ID");
}
